import { m, verbatim, type Message } from '@/i18n';
import type { Verdict } from './constants';
import { meansApplyPlan } from './flow-definition';
import type { MeansPair } from './stage-gates';
import type { Command } from './types';

/**
 * The laneable-attribute canon (docs/VIEWS.md §3a as re-cut 2026-07-22): a
 * board, list, or lens lanes or GROUPS by an **attribute**, and the
 * attribute's values are the lanes. The platform's derived standing tri-state
 * (`open · active · closed` as one bundled word) is RETIRED entirely
 * (docs/CLOSED_AXIS.md, the standing retirement): there is no platform
 * lifecycle word vocabulary - only attributes with value labels that name the
 * primitives, and flow-authored stage words (docs/FLOW_DEFINITION.md §11).
 *
 * This module is the ONE place the axis pick and its value labels live: the
 * lane sources, the group-by shelves, the filter chips' value words, and the
 * lane-drop dispatch all resolve through here. The set is deliberately the
 * item-carried axes of the settable-attribute canon (§11) that enumerate
 * finitely without configuration: the three atoms plus the verdict. A flow's
 * stages stay the flow's own lane source (existing machinery); laning by a
 * field or a tag is a ledgered follow-up (the board item "Lane/group sources
 * beyond the attribute canon"), generalizing the same canon the `means`
 * grammar speaks.
 */
export const LANE_ATTRIBUTES = ['active', 'closed', 'delivered', 'verdict'] as const;
export type LaneAttribute = (typeof LANE_ATTRIBUTES)[number];

/**
 * The default lane/group axis for a flow-less surface (Leon, 2026-07-22):
 * **active** (Idle | Active), not closed - closed items are default-hidden
 * anyway (the `showClosed` gate keeps doing that job), and the question a
 * default surface answers is "is this still in the backlog, or has someone
 * taken it up": the active axis. The closed axis stays a fully available
 * choice, just not the default.
 */
export const DEFAULT_LANE_ATTRIBUTE: LaneAttribute = 'active';

/** The attribute's own name: the QUESTION its values answer, never one of
 *  the values (Leon, 2026-09-15: "Closed: Open" read as nonsense because the
 *  axis wore a value's word). Pickers say "Group by Activity"; headings say
 *  "Activity: Idle" (`laneValueHeading`); the lifecycle chips keep speaking
 *  the values themselves. */
export const LANE_ATTRIBUTE_LABEL: Record<LaneAttribute, Message> = {
  active: m('attributes.name.active'),
  closed: m('attributes.name.closed'),
  delivered: m('attributes.name.delivered'),
  verdict: m('attributes.name.verdict'),
};

/** One value of a laneable attribute: a stable id (the canon words the
 *  `means` grammar speaks: yes/no, none/accepted/disputed) and the value
 *  label that names the primitive. */
export interface LaneAttributeValue {
  id: string;
  /** The value word, as a descriptor the surface formats (docs/I18N.md §6). */
  label: Message;
}

/**
 * The values of each attribute, in canonical lane order (left-to-right on a
 * board, top-down as group sections). Value labels name the primitives - the
 * idle atom-state is a lane like any other (idle is resting, not a
 * deficiency); the verdict reuses the judgment axis's own words (Denied is
 * the product word for the stored `disputed`, 2026-07-18).
 */
export const LANE_ATTRIBUTE_VALUES: Record<LaneAttribute, readonly LaneAttributeValue[]> = {
  active: [
    { id: 'no', label: m('attributes.value.active.no') },
    { id: 'yes', label: m('attributes.value.active.yes') },
  ],
  // The conclusion axis reads the three ENDINGS a lane can hold (Leon,
  // 2026-11-03): Open, Done, Cancelled. `yes` is the done reading, closed and
  // delivered; `cancelled` is closed without a delivery. Review is a chip on
  // an open item, never a fourth lane.
  closed: [
    { id: 'no', label: m('attributes.value.closed.no') },
    { id: 'yes', label: m('attributes.value.closed.yes') },
    { id: 'cancelled', label: m('attributes.value.closed.cancelled') },
  ],
  delivered: [
    { id: 'no', label: m('attributes.value.delivered.no') },
    { id: 'yes', label: m('attributes.value.delivered.yes') },
  ],
  verdict: [
    { id: 'none', label: m('attributes.value.verdict.none') },
    { id: 'accepted', label: m('attributes.value.verdict.accepted') },
    { id: 'disputed', label: m('attributes.value.verdict.disputed') },
  ],
};

/** A stored/URL lane-source key iff it names a laneable attribute. */
export function parseLaneAttribute(value: unknown): LaneAttribute | null {
  return (LANE_ATTRIBUTES as readonly unknown[]).includes(value) ? (value as LaneAttribute) : null;
}

/** The item-carried axes a lane value is read off: the three atoms plus the
 *  verdict (the stored encoding; `open` reads as the `none` value). */
export interface LaneAttributeSnapshot {
  delivered: boolean;
  active: boolean;
  closed: boolean;
  verdict: Verdict;
}

/** The value id an item occupies on an attribute - the lane it is DRAWN into.
 *  Reads the raw atom (never a derived reading): `active` persists through a
 *  close, so a closed+active item lanes as Active on the active axis. */
export function laneValueOf(attr: LaneAttribute, s: LaneAttributeSnapshot): string {
  switch (attr) {
    case 'active':
      return s.active ? 'yes' : 'no';
    case 'closed':
      return !s.closed ? 'no' : s.delivered ? 'yes' : 'cancelled';
    case 'delivered':
      return s.delivered ? 'yes' : 'no';
    case 'verdict':
      return s.verdict === 'open' ? 'none' : s.verdict;
  }
}

/**
 * The HEADING a section or lane wears for an attribute value (Leon,
 * 2026-09-15): the value word alone does not name its axis - "Open" is
 * `closed: no` under one grouping and `verdict: none` under another - so the
 * heading is the attribute-value PAIR: "Activity: Idle", "Conclusion: Open",
 * "Delivery: Delivered", "Verdict: Denied". A single-axis grouping (a flow, a
 * parent, a view, the priority) keeps the value alone; only the attribute
 * family routes through here.
 */
export function laneValueHeading(attr: LaneAttribute, id: string): Message {
  return m('attributes.heading', {
    attribute: LANE_ATTRIBUTE_LABEL[attr],
    value: valueLabel(attr, id),
  });
}

/** The label of the value an item occupies (e.g. "Idle", "Closed"). */
export function laneValueLabelOf(attr: LaneAttribute, s: LaneAttributeSnapshot): Message {
  return valueLabel(attr, laneValueOf(attr, s));
}

/** One value's word; an unknown id (a stale URL) is shown as it is. */
function valueLabel(attr: LaneAttribute, id: string): Message {
  return LANE_ATTRIBUTE_VALUES[attr].find((v) => v.id === id)?.label ?? verbatim(id);
}

/** The single `means` pair a lane stands for: `{attribute: value}`. Null for
 *  an unknown value id (a stale drop target commits nothing). */
export function laneAttributePair(attr: LaneAttribute, valueId: string): MeansPair | null {
  if (attr === 'verdict') {
    return valueId === 'none' || valueId === 'accepted' || valueId === 'disputed'
      ? { attr: 'verdict', value: valueId }
      : null;
  }
  if (attr === 'closed' && valueId === 'cancelled') return { attr: 'closed', value: true };
  if (valueId !== 'yes' && valueId !== 'no') return null;
  return { attr, value: valueId === 'yes' };
}

/**
 * The ordered ACTS a drop into an attribute lane dispatches: exactly the
 * lane's single pair `{attribute: value}`, compiled through the SAME
 * `meansApplyPlan` the flow stages use (docs/FLOW_DEFINITION.md §11 - one act
 * compiler for flow stages AND attribute lanes; it owns the ordering laws,
 * e.g. Reopen before any other act, Close last). A single atom/verdict pair
 * only ever compiles to `dispatch` actions, so the plan flattens to plain
 * commands. Empty = nothing to do (already there, or a stale value id).
 *
 * A `closed: yes` drop is the PLAIN `Close` (no reason): the Cancelled
 * COMPOSITION (Retract? + Close + reason) is an item-page act, never a lane.
 */
export function attributeLaneCommands(
  attr: LaneAttribute,
  valueId: string,
  state: LaneAttributeSnapshot,
): Command[] {
  const pair = laneAttributePair(attr, valueId);
  if (!pair) return [];
  const out: Command[] = [];
  for (const action of meansApplyPlan([pair], state, 'flow:self')) {
    if (action.kind === 'dispatch') out.push(action.command);
  }
  return out;
}
